import { useQueryClient } from '@tanstack/vue-query';
import { ref } from 'vue';

export function useLazyQuery<TData, TParams>({
  queryKey,
  queryFn,
  staleTime
}: {
  queryKey: (params?: TParams) => string[],
  queryFn: (params?: TParams) => Promise<TData>,
  staleTime?: number
}) {
  const isLoading = ref(false);
  const isError = ref<number | null>(null);
  const isSuccess = ref(false);
  const data = ref<TData | null>(null);
  const queryClient = useQueryClient();

  const execute = async (params?: TParams) => {
    try {
      isLoading.value = true;
      isError.value = null;
      
      const result = await queryClient.fetchQuery({
        queryKey: queryKey(params),
        queryFn: () => queryFn(params),
        staleTime: staleTime
      });

      data.value = result;
      isSuccess.value = true;
    } catch (error) {
      data.value = null;
      isError.value = error.code || 500;
      isSuccess.value = false;
    } finally {
      isLoading.value = false;
    }

    return {
      data,
      isError,
      isLoading,
      isSuccess
    };
  }

  return {
    execute,
    data,
    isLoading,
    isError,
    isSuccess
  }
}